<template>
    <div class="configuration-page">
        <b-overlay :show="loading" :rounded="true" no-center>
            <card title="Configuration">
                <template #header-right>
                    <div class="d-flex align-items-center" style="gap: 7px;">
                        <label for="config-filter" class="m-0"><i class="fa fa-search" /></label>
                        <b-input id="config-filter" v-model="query" type="search" placeholder="Filter Options" />
                    </div>
                </template>
                <b-card v-for="group in configurationGroups" :key="group" class="group">
                    <h5 @click="toggleGroup(group)">
                        <i :class="{ 'fa fa-fw': true, 'fa-chevron-right': true, 'fa-rotate-90': isFiltering || openGroups.includes(group) }"
                            aria-hidden="true" style="transition: all .2s;"></i>
                        {{ group }}
                    </h5>
                    <transition name="fade">
                        <div v-if="isFiltering || openGroups.includes(group)" class="d-flex flex-column"
                            style="gap: 12px;">
                            <div class="grid">
                                <div v-for="name in getKeysForGroup(group, false)" :key="name">
                                    <!-- {{ configuration[name] }} -->
                                    <ConfigurationField v-model="configuration[name]" :label="formatConfigName(name)"
                                        :type="getConfigType(name)" :help="fields[name]?.help"
                                        :add-label="fields[name].addLabel" :locked-ids="fields[name].lockedIds"
                                        :options="fields[name]?.options" :append="fields[name]?.append"
                                        :maxlength="fields[name].maxlength" :min="fields[name].min"
                                        :max="fields[name].max" :reorderable="fields[name]?.reorderable" />
                                </div>
                            </div>
                            <div v-for="name in getKeysForGroup(group, true)" :key="name">
                                <ConfigurationField v-model="configuration[name]" :label="formatConfigName(name)"
                                    :type="getConfigType(name)" :help="fields[name]?.help"
                                    :add-label="fields[name].addLabel" :locked-ids="fields[name].lockedIds"
                                    :append="fields[name]?.append" bold-label :max-length="fields[name].maxlength"
                                    :min="fields[name].min" :max="fields[name].max"
                                    :reorderable="fields[name]?.reorderable" />
                            </div>
                        </div>
                    </transition>
                </b-card>

                <div class="actions glass-pane d-flex align-items-center">
                    <div class="flex-fill">
                        <b-button v-if="!isFiltering" @click="toggleGroups" variant="primary">
                            {{ openGroups.length ? 'Close' : 'Open' }} All Configuration Groups
                        </b-button>
                    </div>
                    <div>
                        <b-button variant="success" @click="saveConfiguration" :disabled="!canSave" size="lg">
                            Save Configuration
                        </b-button>
                    </div>
                </div>
            </card>
        </b-overlay>
    </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import axios from "axios";
import { Configuration, autoRefillCadenceFields } from "@/models/Configuration";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import ConfigurationField from "@/components/Configuration/ConfigurationField.vue";
import { UserTask } from "@/models/UserTask";

const entityName = "Configuration";

@Component({
    name: "ConfigurationPage",
    components: {
        ConfigurationField,
    },
})
export default class ConfigurationPage extends Vue {
    protected configuration: any = new Configuration();
    protected loading = false;
    protected taskTemplates: UserTask[] = [];

    get fields(): any {
        return {
            refillTextDays: {
                group: "Refills",
                help: "How often to send refill reminder texts for newer prescriptions (written within the last 90 days). If set to 7, reminders are sent every 7 days (at 7, 14, 21, 28 days before the refill is due).",
            },
            oldScriptRefillTextDays: {
                group: "Refills",
                help: "How often to send refill reminder texts for older prescriptions (written more than 90 days ago). If set to 14, reminders are sent every 14 days (at 14, 28, 42 days before the refill is due). Defaults to 14 if left empty.",
            },
            licenseExpirationAlertDays: {
                title: "License Expiration Alert Days",
                help: "Number of days before patient license expiration to show alert",
                groups: ["Patients"],
                min: 0,
                max: 365,
                type: "number",
            },
            npcTextHours: {
                title: "NPC Text Hours",
                help: "Number of Hours Between texts",
                group: "Text Messaging",
                min: 0,
            },
            awaitingInsuranceHours: {
                title: "Awaiting Insurance Text Hours",
                help: "Number of Hours Between texts",
                group: "Text Messaging",
                min: 0,
            },
            outOfNetworkHours: {
                title: "Out of Network Text Hours",
                help: "Number of Hours Between texts",
                group: "Text Messaging",
                min: 0,
            },
            reversalQueueDays: {
                title: "Reversal Queue Days",
                help: "Number of days after fill date before a prescription becomes eligible for reversal. For example, setting this to 1 means prescriptions will be eligible for reversal 1 day after being filled, while 10 means 10 days after being filled.",
                group: "Billing",
                type: "number",
                min: 0,
            },
            initialRefillDays: {
                title: "Refill Cadence",
                help: "Days to calculated end of Day Supply. Negative numbers are before the end of the day supply.",
                group: "Text Messaging",
                type: "number-array",
            },
            deliveryConfirmationDays: {
                title: "Delivery Confirmation",
                help: "Number of Days Post Delivery. Negative numbers will be ignored.",
                group: "Text Messaging",
                type: "number-array",
            },
            ordersAllowPV1Prescriptions: {
                title: "Allow PV1 Prescriptions",
                help: "Allow Prescriptions that require PV1, but which have not yet been completed, to be put on orders.",
                group: "Orders",
            },
            requireExplicitVerificationPV1: {
                title: "Require Explicit Verification PV1",
                group: "Prescriptions",
                help: "When enabled, pharmacists must explicitly verify PV1 by clicking a verification button before prescriptions can proceed.",
            },
            requireExplicitVerificationPV2: {
                title: "Require Explicit Verification PV2",
                group: "Prescriptions",
                help: "When enabled, pharmacists must explicitly verify PV2 by clicking a verification button before prescriptions can be finalized.",
            },
            useLastForDiffCheck: {
                title: "Use Last Verification for Difference Check",
                group: "Prescriptions",
                help: "When enabled, compares prescription changes against the most recent verification (PV1 or PV2) instead of always using PV1.",
            },
            hideDispensingFields: {
                title: "Hide Dispensing Fields",
                group: "Prescriptions",
                help: "Hides the lot number, drug expiration, serial number, and partner fill fields from prescription details.",
            },
            autoRefillPromptStatement: {
                group: "Auto Refill",
                type: "textarea",
                help: "The automated prompt statement played to patients during the IVR (Interactive Voice Response) auto refill phone system in English. This message explains the auto refill program to patients.",
            },
            autoRefillPromptStatementSpanish: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Spanish.",
            },
            autoRefillPromptStatementRussian: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Russian.",
            },
            autoRefillPromptStatementSomali: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Somali.",
            },
            autoRefillPromptStatementArabic: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Arabic.",
            },
            autoRefillPromptStatementChineseSimplified: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Simplified Chinese.",
            },
            autoRefillPromptStatementVietnamese: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Vietnamese.",
            },
            autoRefillPromptStatementFarsi: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Farsi (Persian).",
            },
            autoRefillPromptStatementKorean: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Korean.",
            },
            autoRefillPromptStatementRomanian: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Romanian.",
            },
            autoRefillPromptStatementSwahili: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Swahili.",
            },
            autoRefillPromptStatementBurmese: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Burmese.",
            },
            autoRefillPromptStatementNepali: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Nepali.",
            },
            autoRefillPromptStatementAmharic: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Amharic.",
            },
            autoRefillPromptStatementPashto: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Pashto.",
            },
            autoCaseAppNewPatientCreation: {
                group: "Cases",
                help: "When enabled, automatically creates a case when a new patient account is created through the patient app, allowing staff to review and verify the new patient information.",
            },
            autoCaseTextsNoValidNumbers: {
                groups: ["Cases", "Text Messaging"],
                help: "When enabled, automatically creates a case if a patient has no valid phone numbers for text messaging. This helps identify and resolve patient contact information issues.",
            },
            autoCaseTextsPatientContactFailure: {
                groups: ["Cases", "Text Messaging"],
                help: "When enabled, automatically creates a case when attempts to contact a patient fail repeatedly. This helps ensure important communications are not missed.",
            },
            autoCaseTextsFailAutoRefill: {
                groups: ["Cases", "Text Messaging", "Auto Refill"],
                help: "Automatically creates a patient case if one doesn't already exist if texts fail to send for an auto refill.",
            },
            partialPaymentTextThresholdAmount: {
                groups: ["Text Messaging"],
                help: "When amount owed is over this amount, text will refer patient to call instead of sending a web link if the order is put into partial payment status.",
                min: 0,
            },
            npcTextThresholdAmount: {
                groups: ["Text Messaging"],
                title: "NPC Text Threshold Amount",
                help: "When amount owed is over this amount, text will refer patient to call instead of sending a web link if it is a new prescription.",
                min: 0,
            },
            infinitrakApiKey: {
                title: "InfiniTRAK API Key",
                groups: ["Integrations"],
                help: "API key for integrating with the InfiniTRAK inventory management system. This enables automatic inventory synchronization and tracking.",
            },
            irsReportEmail: {
                hidden: true,
            },
            disableMedGuidePrinting: {
                title: "Disable MedGuide Printing",
                groups: ["Printing"],
                help: "When enabled, prevents automatic printing of medication guides with prescriptions. You can disable this if you are manually printing drug medication guides or do not require them.",
            },
            inventoryReasons: {
                type: "string-array",
                groups: ["Inventory"],
                addLabel: "Add Reason",
                maxlength: 40,
                reorderable: true,
                help: "Define custom reasons for inventory adjustments. These are used when adding or removing inventory to document why the adjustment was made, improving inventory tracking and auditing.",
            },
            textMessageTemplates: {
                type: "text-message-templates",
                title: "Text Messages",
                groups: ["Text Messaging"],
                fullWidth: true,
                help: "Create and manage custom SMS message templates for various pharmacy workflows. Templates support dynamic tokens that are replaced with patient and prescription information when messages are sent.",
            },
            emailTemplates: {
                type: "email-templates",
                title: "Email Templates",
                groups: ["Email"],
                fullWidth: true,
                help: "Create and manage custom email templates for patient communications. Templates support dynamic tokens and HTML formatting for professional, branded emails. Emails use SendGrid. Additional system requirements apply. Contact Scriptly support for details.",
            },
            readyForPickupTexts: {
                title: "Ready for Pickup Texts",
                groups: ["Text Messaging"],
                help: "When enabled, automatically sends text messages to patients when their prescriptions are ready for pickup at the pharmacy. This can be disabled at the Store configuration level.",
            },
            disableWebNotifications: {
                title: "Disable System Presented Notifications",
                groups: ["Notifications"],
                help: "Disables presenting notifications using native operating system notifications (ie, Windows notifications). Notifications will still appear within the application as a fallback.",
            },
            orderCancellationReasons: {
                type: "string-array",
                groups: ["Orders"],
                addLabel: "Add Cancellation Reason",
                maxlength: 40,
                reorderable: true,
                help: "Define custom reasons for why orders are cancelled. These help track cancellation patterns and improve order fulfillment processes.",
            },
            requireLotsOnInventoryBatches: {
                groups: ["Inventory"],
                help: "When enabled, requires lot numbers to be entered when processing inventory batches. This ensures proper tracking of medication lots for safety and recall purposes.",
            },
            blockDeactivatingDrugIfInventoryOnHand: {
                groups: ["Inventory"],
                help: "When enabled, prevents deactivating drugs that have inventory on hand. This helps avoid accidentally deactivating drugs that are still in stock and available for dispensing.",
            },
            inventoryBatchSubmitEndpoint: {
                groups: ["Inventory"],
                help: "Pushes inventory batch data including any lots and expiry dates to provided endpoint.",
            },
            allowAutorefillWithoutNotification: {
                groups: ["Notifications", "Auto Refill"],
                help: "When enabled, allows auto refill orders to be processed even if patient notification (texts) fail or is not completed. Use with caution as patients may not be aware of upcoming refills.",
            },
            allowAutorefillWithoutPayment: {
                groups: ["Auto Refill"],
                help: "When enabled, allows auto refill orders to be processed even if payment has not been collected. This creates a balance due (partial payment) on the patient's order that must be collected before dispensing can occur.",
            },
            onHoldSubStatuses: {
                groups: ["Prescriptions"],
                type: "name-active-field",
                lockedIds: [2, 6, 8, 13, 14],
                addLabel: "Add Sub Status",
                reorderable: true,
                help: "Define custom sub-status reasons for prescriptions that are on hold. These provide additional detail about why a prescription is on hold and help with reporting and workflow management. Some system-defined sub-statuses cannot be removed/modified.",
            },
            discontinuedSubStatuses: {
                groups: ["Prescriptions"],
                type: "name-active-field",
                lockedIds: [],
                addLabel: "Add Sub Status",
                reorderable: true,
                help: "Define custom sub-status reasons for discontinued prescriptions. These provide additional detail about why a prescription was discontinued and help with reporting and patient communication.",
            },
            faxCoverPage: {
                groups: ["Faxing"],
                help: "When enabled, automatically includes a cover page with ALL outbound faxes. Cover page content requires setup by Scriptly support.",
            },
            faxRoutingRules: {
                groups: ["Faxing"],
                help: "Enables automatic program routing based on faxes you received to the numbers configured here. (The number must be on the Documo account configured for your instance.) Fax Numbers should start with +1 followed by the 10 digit number. (eg. +19999999999) Contact Scriptly support for assistance setting up fax routing rules.",
                type: "fax-routing-rule",
            },
            faxTemplateOverrides: {
                title: "Fax Templates",
                groups: ["Faxing"],
                type: "fax-templates",
                fullWidth: true,
                help: "Configure custom fax templates to override the default system templates. These overrides will be used across all stores and programs unless they have their own overrides.",
            },
            patientAppCheckoutAttestation: {
                groups: ["Orders"],
                type: "textarea",
                help: "The attestation text that appears on the checkout page in the patient app.<br><br><strong>Tokens:</strong><ul><li>{{AuthorizeBlock}}: will be replaced with 'I, Patient Name, authorize Pharmacy Name'</li><li>{{PharmacyName}}: will be replaced with the pharmacy name.</li></ul>",
            },
            requireExplicitDur: {
                title: "Require Explicit DUR Acknowledgment",
                type: "select",
                options: [
                    {
                        value: null,
                        text: "Not Required",
                    },
                    {
                        value: 2,
                        text: "Major Severity Only",
                    },
                    {
                        value: 1,
                        text: "Moderate and Major Severity Only",
                    },
                    {
                        value: 0,
                        text: "All Severities",
                    },
                ],
                groups: ["Prescriptions"],
                help: "Require pharmacists to explicitly acknowledge Drug Utilization Review (DUR) alerts based on severity level. This ensures critical drug interactions and clinical alerts are not overlooked.",
            },
            requireDurNotes: {
                title: "Require DUR Notes on Explicit DUR",
                groups: ["Prescriptions"],
                help: "When enabled, pharmacists must enter notes explaining their decision when acknowledging DUR (Drug Utilization Review) alerts. This provides documentation for clinical decisions.",
            },
            enableRTPB: {
                groups: ["Prescriptions"],
                title: "Enable Real-Time Prescription Benefit (RTPB)",
                help: "When enabled, adds a 'Check Benefits' button to prescriptions for getting real-time benefits information. Contact Scriptly support for configuration and details.",
            },
            patientDocumentTags: {
                type: "string-array",
                groups: ["Patients"],
                maxlength: 40,
                reorderable: true,
                help: "Define custom tags for categorizing patient documents. Tags help organize and quickly find specific types of documents such as ID cards, insurance forms, prescriptions, or medical records.",
            },
            saveTransferDocumentToPatientDocuments: {
                title: "Transfer Document to Patient Documents",
                groups: ["Patients"],
                help: "Save transfer faxes to patient documents with a 'Transfer' tag automatically.",
            },
            requirePatientDocumentTags: {
                title: "Require Patient Document Tags",
                groups: ["Patients"],
                type: "boolean",
                help: "Require at least one tag when adding patient documents. (only when tags are available)",
            },
            hideIncompleteConsultationsOnPrintQueue: {
                title: "Hide Patients with Incomplete Consultations on Print Queue",
                groups: ["Consultations"],
                help: "When enabled, patients with incomplete pharmacist consultations will not appear in the print queue until all consultation requirements are completed.",
            },
            hideIncompleteAnswersOnPrintQueue: {
                title: "Hide Prescriptions with Incomplete Answers on Print Queue",
                groups: ["Printing"],
                type: "boolean",
                help: "When enabled, prescriptions with incomplete clinical screening questions will not appear in the print queue until all required questions are answered. Configure questions in the Categories section.",
            },
            transferReasons: {
                groups: ["Prescriptions"],
                type: "name-active-field",
                addLabel: "Add Transfer Reason",
                reorderable: true,
                help: "Define custom reasons for why prescriptions are being transferred to or from your pharmacy. These help with tracking and reporting prescription transfer patterns.",
            },
            patientAssistanceProgramRejectReasons: {
                type: "string-array",
                groups: "Patient Assistance Program",
                maxlength: 40,
                reorderable: true,
                help: "Define custom reasons for why patient assistance program applications are rejected. These help track common rejection causes and improve future application success rates.",
            },
            papNextTouchDays: {
                title: "PAP Next Touch Days",
                groups: "Patient Assistance Program",
                fullWidth: false,
                type: "pap-next-touch",
                help: "Number of days before the next required touchpoint",
            },
            priorAuthStatusReasons: {
                title: "Prior Authorization Status Reasons",
                groups: ["Prior Authorization"],
                fullWidth: false,
                type: "prior-auth-status-reasons",
                reorderable: true,
                help: "Define custom status reasons for prior authorization requests at each stage of the process. These provide detailed context about the current state and help track approval patterns.",
            },
            priorAuthNextTouchDays: {
                title: "Prior Authorization Next Touch Days",
                groups: ["Prior Authorization"],
                fullWidth: true,
                type: "prior-auth-next-touch",
                help: "Configure automated follow-up schedules for prior authorization requests based on their status. This ensures timely follow-ups and prevents authorizations from expiring.",
            },
            priorAuthNoteTemplates: {
                title: "Prior Authorization Note Templates",
                type: "string-array",
                groups: ["Prior Authorization"],
                maxlength: 50,
                reorderable: true,
                help: "Create quick-select note templates for common prior authorization scenarios. Templates speed up documentation and ensure consistent communication.",
            },
            doctorFollowUpNextTouchDays: {
                title: "Doctor Follow Up Next Touch Days",
                groups: ["Doctor Follow Up"],
                type: "doctor-follow-up-next-touch",
                help: "Configure automated follow-up schedules for contacting prescribers based on the status of follow-up items. This ensures more efficient use of the doctor follow-up queue.",
            },
            doctorFollowUpMaxDays: {
                title: "Doctor Follow Up Max Days",
                help: "The maximum number of days before the follow up item is considered expired, and abandoned.",
                groups: ["Doctor Follow Up"],
                type: "number",
                min: 0
            },
            planTypeCanBeUnknown: {
                title: "Plan Type Can Be Unknown",
                groups: ["Insurance"],
                help: "When enabled, allows the plan type to be unknown when creating a new plan.",
            },
            caseProblems: {
                title: "Case Problems",
                groups: ["Cases"],
                type: "string-array",
                maxlength: 50,
                reorderable: true,
                help: "Define custom problem types that can be assigned to patient cases. These help categorize and track the nature of issues that need to be resolved, improving case management and reporting.",
            },
            pharmacistOnlyNotes: {
                groups: ["Notes"],
                help: "When enabled, creates a special note type that only pharmacists can view. This is useful for clinical observations or sensitive information that should not be visible to all staff.",
            },
            excludeRefillRequestsFromNotes: {
                title: "Exclude refill requests from notes",
                groups: ["Notes"],
                type: 'boolean',
                help: "When enabled, refill request entries will not appear in the patient notes timeline. This reduces clutter in patient notes by hiding routine refill activities.",
            },
            createTaskOnMPPPClaimFromTaskTemplateId: {
                title: "Create Task from Template on MPPP Claim",
                groups: ["Tasks"],
                type: "task-template",
                help: "Automatically create a task when an MPPP (Medicare Prescription Payment Plan) claim is received. Select a task template to define the task details and assignment.",
            },
            createTaskOnMPPPEligibilityFromTaskTemplateId: {
                title: "Create Task from Template on MPPP Eligibility",
                groups: ["Tasks"],
                type: "task-template",
                help: "Automatically create a task when MPPP (Medicare Prescription Payment Plan) eligibility is determined. Select a task template to define the task details and assignment.",
            },
            providerFollowUpResponseTeamId: {
                title: "Provider Portal Follow Up Response Team",
                groups: ["Tasks"],
                type: "team",
                help: "Select the team that will be assigned tasks when providers submit follow-up responses through the provider portal. This ensures responses are routed to the appropriate staff.",
            },
            voicemailNotificationTeamId: {
                title: "Voicemail Notification Team",
                groups: ["Notifications"],
                type: "team",
                help: "Team that will receive notifications when new voicemails are created"
            },
            allowMultipleTaskAssignments: {
                title: "Allow Multiple Task Assignments",
                groups: ["Tasks"],
                type: "boolean",
                help: "When enabled, allows tasks to be assigned to multiple users or teams simultaneously. When disabled, tasks can only be assigned to a single user or team at a time.",
            },
            disableFutureWrittenDates: {
                title: "Disable Future Written Dates",
                help: "Prevents setting prescription written dates to future dates. Super admins can always use future dates.",
                groups: ["Prescriptions"],
                type: "boolean"
            },
            parseMiddleNameFromEscript: {
                title: "Parse Middle Name from E-Script",
                help: "When enabled, attempts to extract and store the middle name from e-script patient names.",
                groups: ["Prescriptions"],
                type: "boolean"
            },
            ...autoRefillCadenceFields(this.configuration.autoRefillNextProcessDateDefaultCadenceType),
            autoRefillCopayDifferenceAllowance: {
                title: "Copay Difference Allowance",
                help: "The amount a copay can increase by before preventing autorefill from being processed.",
                groups: ["Auto Refill"],
                type: "number",
                min: 0,
            },
            autoRefillAutoProcessAuditChance: {
                title: "Chance of Audit",
                help: "The chance an autoprocessed autorefill order goes into audit. 100% = always audit, 0% = never audit.",
                groups: ["Auto Refill"],
                type: "number",
                min: 0,
                max: 100,
            },
            dispenseErrorLabel: {
                title: "Error Label",
                groups: ["Prescriptions"],
                type: "string",
                help: "Rename the 'Dispense Error' page to a custom label that better fits your pharmacy's workflow.",
            },
            prescriberFollowUpNoteTemplates: {
                title: "Doctor Follow Up Note Templates",
                type: "string-array",
                groups: ["Doctor Follow Up"],
                maxlength: 50,
                help: "Create quick-select note templates for common prescriber communication scenarios. Templates speed up documentation when following up with healthcare providers.",
            },
            specialities: {
                title: "Doctor Specialities",
                type: "specialities",
                groups: ["Doctor"],
                help: "Manage the list of medical specialties used for categorizing prescribers.",
            },
            allowShippingControlledWithoutPdmp: {
                title: "Allow Shipping Controlled Without PDMP Reporting",
                groups: ["Prescriptions"],
                help: "When enabled, allows controlled substance prescriptions to be shipped even if PDMP (Prescription Drug Monitoring Program) reporting has not enabled for a state. Use with caution as this may affect regulatory compliance.",
            },
            autoOutboundCadence: {
                title: "Auto Outbound Cadence",
                help: "Number of seconds between each auto outbound call attempt. Lower values means more calls going out at the same time. Consider your staffing schedules when adjusting this value.",
                type: "number",
                groups: ["Auto Outbound"],
                fullWidth: false,
            },
            autoOutboundStatusConfigurations: {
                title: "Auto Outbound Status Configurations",
                type: "auto-outbound-status-configuration",
                groups: ["Auto Outbound"],
                fullWidth: true,
                help: "Configure automated outbound calling behavior for different prescription statuses. This defines when and how the system should automatically call patients based on their prescription status.",
            },
            autoOutboundPartialPaymentConfiguration: {
                title: "Auto Outbound Partial Payment Configuration",
                type: "auto-outbound-configuration",
                groups: ["Auto Outbound"],
                fullWidth: true,
                help: "Configure automated outbound calling settings for patients with partial payment balances. This includes timing, messaging, and retry logic for payment collection calls.",
            },
            autoOutboundDeliveryConfirmationConfiguration: {
                title: "Auto Outbound Delivery Confirmation Configuration",
                type: "auto-outbound-configuration",
                groups: ["Auto Outbound"],
                fullWidth: true,
                help: "Configure automated outbound calling settings for delivery confirmations. This includes timing and messaging for calls confirming prescription deliveries with patients.",
            },
            rxTransferDeniedSubStatusId: {
                title: "Rx Transfer Denied Sub Status",
                type: "select",
                options: [
                    {
                        value: null,
                        text: "",
                    },
                    ...this.configuration.onHoldSubStatuses.map((i: any) => ({ value: i.id, text: i.name }))
                ],
                groups: ["Transfer"],
                help: "Select the on-hold sub-status to automatically apply when an electronic prescription transfer is denied by the external pharmacy. This helps track denied transfers and follow up appropriately via the on-hold queue page.",
            },
            rxTransferDeniedTaskTemplateId: {
                title: "Rx Transfer Denied Task Template",
                type: "select",
                help: "If a template is selected, a new task will be created based on the template when an electronic transfer is denied by the external pharmacy.",
                options: [
                    {
                        value: null,
                        text: "",
                    },
                    ...this.taskTemplates.map(template => ({ value: template.id, text: template.title }))
                ],
                groups: ["Transfer"]
            },
            requirePharmacistOnExternalTransfers: {
                title: "Require Pharmacist on External Transfers",
                groups: ["Transfer"],
                type: "boolean",
                help: "When enabled, requires a pharmacist to be selected for all external transfers. When disabled, pharmacist is optional for pharmacies that support electronic RxTransfer."
            },
            providerPortalAccountCreationTaskTemplateId: {
                title: "Provider Portal Account Creation Task Template",
                type: "select",
                help: "If a template is selected, a new task will be created based on the template when a provider requests an account.",
                options: [
                    {
                        value: null,
                        text: "",
                    },
                    ...this.taskTemplates.map(template => ({ value: template.id, text: template.title }))
                ],
                groups: ["Provider Portal"]
            },
            entityAlertConfigurations: {
                title: "Entity Alert Configurations",
                type: "entity-alert-configuration",
                groups: ["Alerts"],
                fullWidth: true,
                help: "Configure individual alert settings including colors, display styles, and enable/disable functionality."
            },
        };
    }

    get filteredFields() {
        if (!this.isFiltering) return this.fields;
        const fields: any = {};

        Object.keys(this.fields).forEach(f => {
            const data = this.fields[f];
            const name = this.formatConfigName(f);

            if (!name.toLowerCase().includes(this.query.toLowerCase())) return;

            fields[f] = data;
        });

        return fields;
    }

    private openGroups: Array<string> = [];
    private query: string = "";

    // noinspection JSUnusedGlobalSymbols
    mounted() {
        this.fetchConfiguration();
        this.fetchTaskTemplates();
    }

    get isFiltering() {
        return this.query?.trim();
    }

    get configKeys() {
        return Object.keys(this.filteredFields)
            .filter(i => this.filteredFields[i]?.hidden !== true);
    }

    get configKeysGrid() {
        return Object.keys(this.configuration)
            .filter(i => this.filteredFields[i]?.hidden !== true && !this.filteredFields[i]?.fullWidth);
    }

    get configKeysFullWidth() {
        return Object.keys(this.configuration)
            .filter(i => this.filteredFields[i]?.hidden !== true && this.filteredFields[i]?.fullWidth);
    }

    get canSave() {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Admin/Configuration".toLowerCase());
    }

    formatConfigName(name: string): string {
        const explicitTitle = this.fields[name]?.title;
        if (explicitTitle) return explicitTitle;

        let resp = name.replace(/([A-Z])/g, " $1");
        resp = resp.charAt(0).toUpperCase() + resp.slice(1);
        resp = resp.replace(" Api ", " API ");

        return resp;
    }

    getConfigType(name: string): string {
        const explicitType = this.filteredFields[name]?.type;
        if (explicitType) return explicitType as string;

        return this.configuration.typeOf(name);
    }

    async fetchConfiguration() {
        try {
            this.loading = true;
            const fetchResponse = await axios.get<Configuration>("/api/Configuration");
            this.configuration = new Configuration(fetchResponse.data);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }
    }

    async saveConfiguration() {
        try {
            this.loading = true;
            if (!this.configuration.autoRefillNextProcessDateDefaultCadence) {
                this.configuration.autoRefillNextProcessDateDefaultCadence = null;
            }
            this.cleanConfigurationValues();

            const saveResponse = await axios.post<Configuration>("/Configuration", this.configuration);
            this.configuration = new Configuration(saveResponse.data);
            this.$notification(NotificationOptions.successSaveNotificationPreset(entityName));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }
    }

    cleanConfigurationValues() {
        Object.keys(this.fields).forEach(k => {
            //Remove any empty strings from string array configs
            if (this.fields[k].type === "string-array") {
                this.configuration[k] = this.configuration[k]?.filter((v: string) => v.trim());
            }
        });
    }

    getGroupsForKey(key: string): string[] {
        const fieldDef = this.filteredFields[key];
        if (!fieldDef) return ["Other"];
        if (fieldDef.group) return [fieldDef.group];
        if (fieldDef.groups) return fieldDef.groups;
        if (Array.isArray(fieldDef)) return fieldDef;
        return [fieldDef];
    }

    get configurationGroups() {
        return [...new Set(this.configKeys.flatMap(i => this.getGroupsForKey(i)))].toSorted();
    }

    getKeysForGroup(group: string, fullWidth: boolean) {
        if (fullWidth) {
            return this.configKeysFullWidth.filter(i => this.getGroupsForKey(i).includes(group));
        }
        return this.configKeysGrid.filter(i => this.getGroupsForKey(i).includes(group));
    }

    toggleGroup(group: string) {
        if (this.isFiltering) return;
        if (this.openGroups.includes(group)) {
            this.openGroups = this.openGroups.filter(og => og !== group);
        } else {
            this.openGroups.push(group);
        }
    }

    toggleGroups() {
        if (this.openGroups.length) {
            this.openGroups = [];
        } else {
            this.openGroups = [...this.configurationGroups];
        }
    }

    async fetchTaskTemplates() {
        try {
            const fetchResponse = await axios.get<UserTask[]>("/api/Task/templates");
            this.taskTemplates = fetchResponse.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {

        }
    }
}
</script>

<style scoped>
.group {
    box-shadow: none;
}

.group:first-child {
    margin-top: 0;
}

.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
    gap: 15px;
    margin-top: 10px;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity .2s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

h5 {
    cursor: pointer;
    margin-bottom: 0;
}

.actions {
    position: sticky;
    bottom: 0;
    z-index: 900;
}

@media screen and (min-width: 1200px) {
    .grid {
        grid-template-columns: repeat(3, 1fr);
    }
}
</style>
